import React, { useState, Fragment } from 'react';
import { ArrowUpRight, ArrowDownLeft, ChevronDown, ChevronUp, Download, Filter, Wallet, Clock, CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const transactions = [{
  id: 'TX-9021',
  customer: 'Global Tech Solutions',
  date: 'Oct 14, 2023',
  retail: 1250,
  fee: 350,
  earnings: 900,
  status: 'Settled'
}, {
  id: 'TX-9020',
  customer: 'Toronto Creative Hub',
  date: 'Oct 13, 2023',
  retail: 450,
  fee: 120,
  earnings: 330,
  status: 'Processing'
}, {
  id: 'TX-9019',
  customer: 'New York FinTech',
  date: 'Oct 12, 2023',
  retail: 2100,
  fee: 550,
  earnings: 1550,
  status: 'Settled'
}, {
  id: 'TX-9018',
  customer: 'North Star Logistics',
  date: 'Oct 11, 2023',
  retail: 85,
  fee: 25,
  earnings: 60,
  status: 'Settled'
}, {
  id: 'TX-9017',
  customer: 'Vancouver Tours Ltd',
  date: 'Oct 10, 2023',
  retail: 650,
  fee: 180,
  earnings: 470,
  status: 'Failed'
}];
const payoutHistory = [{
  id: 'PO-8821',
  date: 'Oct 01, 2023',
  amount: 3200,
  method: 'Bank Transfer • **** 4291',
  status: 'Settled'
}, {
  id: 'PO-8820',
  date: 'Sep 15, 2023',
  amount: 2850,
  method: 'Bank Transfer • **** 4291',
  status: 'Settled'
}, {
  id: 'PO-8819',
  date: 'Sep 01, 2023',
  amount: 4100,
  method: 'Bank Transfer • **** 4291',
  status: 'Settled'
}];
const availableBalance = transactions.reduce((acc, tx) => acc + tx.earnings, 0);
const pendingClear = 5000; // Static value or computed separately if required
export function Transactions() {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [payoutFreq, setPayoutFreq] = useState('Weekly');
  return <div className="space-y-6 max-w-[1600px] mx-auto" data-id="element-1464">
      <div className="flex items-center justify-between bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-1465">
        <div data-id="element-1466">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-1467">
            Transparency Hub
          </h1>
          <p className="text-sm text-gray-500 font-medium" data-id="element-1468">
            Money In: Real-time profit splits and automated payout tracking.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-1469">
          <button className="px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-bold text-gray-600 hover:bg-gray-50 transition-all flex items-center gap-2 shadow-sm" data-id="element-1470">
            <Download size={18} data-id="element-1471" />
            Export Ledger
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1472">
        {/* Payout Request Module */}
        <div className="bg-slate-900 p-8 rounded-lg text-white space-y-8 relative overflow-hidden" data-id="element-1473">
          <div className="relative z-10" data-id="element-1474">
            <div className="flex items-center justify-between mb-8" data-id="element-1475">
              <div className="p-3 bg-white/10 rounded-lg" data-id="element-1476">
                <Wallet size={24} className="text-indigo-400" data-id="element-1477" />
              </div>
              <div className="flex bg-white/5 p-1 rounded-lg border border-white/10" data-id="element-1478">
                {['Daily', 'Weekly', 'Monthly'].map(f => <button key={f} onClick={() => setPayoutFreq(f)} className={`px-3 py-1 text-[10px] font-bold rounded-md transition-all ${payoutFreq === f ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400 hover:text-white'}`} data-id="element-1479">
                    {f}
                  </button>)}
              </div>
            </div>

            <div className="space-y-1" data-id="element-1480">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-1481">
                Available for Payout
              </p>
              <h2 className="text-4xl font-bold" data-id="element-1482">
                ${availableBalance.toLocaleString()}
              </h2>
            </div>

            <div className="mt-8 space-y-4" data-id="element-1483">
              <button className="w-full py-3 bg-indigo-600 text-white rounded-lg text-sm font-bold shadow-lg shadow-indigo-500/20 hover:bg-indigo-700 transition-all" data-id="element-1484">
                Request Payout
              </button>
              <div className="space-y-2" data-id="element-1485">
                <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest" data-id="element-1486">
                  <span className="text-slate-400" data-id="element-1487">Funds Clearing</span>
                  <span className="text-indigo-400" data-id="element-1488">75% Complete</span>
                </div>
                <div className="w-full bg-white/10 h-1.5 rounded-full overflow-hidden" data-id="element-1489">
                  <div className="h-full bg-indigo-500 w-3/4" data-id="element-1490"></div>
                </div>
                <p className="text-[10px] text-slate-500 font-medium" data-id="element-1491">
                  Next clearing cycle in 3 days.
                </p>
              </div>
            </div>
          </div>
          <div className="absolute -right-8 -bottom-8 opacity-5" data-id="element-1492">
            <Wallet size={200} data-id="element-1493" />
          </div>
        </div>

        {/* Transactions Table */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col" data-id="element-1494">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50" data-id="element-1495">
            <h3 className="font-bold text-slate-900 text-sm" data-id="element-1496">
              Profit Split Ledger
            </h3>
            <div className="flex gap-2" data-id="element-1497">
              <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1498">
                <Filter size={18} data-id="element-1499" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-x-auto" data-id="element-1500">
            <table className="w-full text-left border-collapse" data-id="element-1501">
              <thead data-id="element-1502">
                <tr className="text-[10px] font-bold uppercase tracking-wider text-gray-400 border-b border-gray-100" data-id="element-1503">
                  <th className="px-6 py-4" data-id="element-1504">Transaction</th>
                  <th className="px-6 py-4" data-id="element-1505">Customer</th>
                  <th className="px-6 py-4" data-id="element-1506">Profit Split</th>
                  <th className="px-6 py-4" data-id="element-1507">Status</th>
                  <th className="px-6 py-4 w-10" data-id="element-1508"></th>
                </tr>
              </thead>
              <tbody className="text-sm" data-id="element-1509">
                {transactions.map(tx => <Fragment key={tx.id} data-id="element-1510">
                    <tr onClick={() => setExpandedRow(expandedRow === tx.id ? null : tx.id)} className={`hover:bg-gray-50 cursor-pointer transition-colors group ${expandedRow === tx.id ? 'bg-indigo-50/30' : ''}`} data-id="element-1511">
                      <td className="px-6 py-4" data-id="element-1512">
                        <p className="font-bold text-slate-900" data-id="element-1513">{tx.id}</p>
                        <p className="text-[10px] font-bold text-gray-400 uppercase" data-id="element-1514">
                          {tx.date}
                        </p>
                      </td>
                      <td className="px-6 py-4 font-bold text-slate-700" data-id="element-1515">
                        {tx.customer}
                      </td>
                      <td className="px-6 py-4" data-id="element-1516">
                        <div className="flex items-center gap-2" data-id="element-1517">
                          <span className="text-gray-400 font-medium" data-id="element-1518">
                            ${tx.retail.toLocaleString()}
                          </span>
                          <span className="text-gray-300 text-[10px] font-bold" data-id="element-1519">
                            RETAIL
                          </span>
                          <ArrowRight size={12} className="text-gray-300" data-id="element-1520" />
                          <span className="text-red-400 font-medium" data-id="element-1521">
                            -${tx.fee.toLocaleString()}
                          </span>
                          <span className="text-gray-300 text-[10px] font-bold" data-id="element-1522">
                            FEE
                          </span>
                          <ArrowRight size={12} className="text-gray-300" data-id="element-1523" />
                          <span className="text-indigo-600 font-bold" data-id="element-1524">
                            ${tx.earnings.toLocaleString()}
                          </span>
                          <span className="text-indigo-300 text-[10px] font-bold uppercase" data-id="element-1525">
                            Profit
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-1526">
                        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${tx.status === 'Settled' ? 'bg-emerald-50 text-emerald-600' : tx.status === 'Processing' ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'}`} data-id="element-1527">
                          {tx.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-300 group-hover:text-indigo-400 transition-colors" data-id="element-1528">
                        {expandedRow === tx.id ? <ChevronUp size={18} data-id="element-1529" /> : <ChevronDown size={18} data-id="element-1530" />}
                      </td>
                    </tr>
                    <AnimatePresence data-id="element-1531">
                      {expandedRow === tx.id && <tr data-id="element-1532">
                          <td colSpan={5} className="p-0" data-id="element-1533">
                            <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} className="overflow-hidden bg-gray-50/50 border-b border-gray-100" data-id="element-1534">
                              <div className="px-12 py-6 grid grid-cols-3 gap-8" data-id="element-1535">
                                <div className="space-y-1" data-id="element-1536">
                                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest" data-id="element-1537">
                                    Retail Price
                                  </p>
                                  <p className="text-lg font-bold text-slate-900" data-id="element-1538">
                                    ${tx.retail.toLocaleString()}.00
                                  </p>
                                  <p className="text-[10px] text-gray-500 font-medium" data-id="element-1539">
                                    Paid via Stripe Canada
                                  </p>
                                </div>
                                <div className="space-y-1" data-id="element-1540">
                                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest" data-id="element-1541">
                                    NiishEngine Fee
                                  </p>
                                  <p className="text-lg font-bold text-red-500" data-id="element-1542">
                                    -${tx.fee.toLocaleString()}.00
                                  </p>
                                  <p className="text-[10px] text-gray-500 font-medium" data-id="element-1543">
                                    Infrastructure & Fulfillment
                                  </p>
                                </div>
                                <div className="space-y-1" data-id="element-1544">
                                  <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest" data-id="element-1545">
                                    Your Net Profit
                                  </p>
                                  <p className="text-lg font-bold text-indigo-600" data-id="element-1546">
                                    ${tx.earnings.toLocaleString()}.00
                                  </p>
                                  <p className="text-[10px] text-indigo-400 font-medium" data-id="element-1547">
                                    Available in 48 hours
                                  </p>
                                </div>
                              </div>
                            </motion.div>
                          </td>
                        </tr>}
                    </AnimatePresence>
                  </Fragment>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Payout History */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col" data-id="element-1548">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50" data-id="element-1549">
          <h3 className="font-bold text-slate-900 text-sm" data-id="element-1550">Payout History</h3>
          <button className="text-xs font-bold text-indigo-600 hover:underline" data-id="element-1551">
            View All Payouts
          </button>
        </div>
        <div className="overflow-x-auto" data-id="element-1552">
          <table className="w-full text-left border-collapse" data-id="element-1553">
            <thead data-id="element-1554">
              <tr className="text-[10px] font-bold uppercase tracking-wider text-gray-400 border-b border-gray-100" data-id="element-1555">
                <th className="px-6 py-4" data-id="element-1556">Payout ID</th>
                <th className="px-6 py-4" data-id="element-1557">Date</th>
                <th className="px-6 py-4" data-id="element-1558">Amount</th>
                <th className="px-6 py-4" data-id="element-1559">Method</th>
                <th className="px-6 py-4" data-id="element-1560">Status</th>
              </tr>
            </thead>
            <tbody className="text-sm" data-id="element-1561">
              {payoutHistory.map(po => <tr key={po.id} className="hover:bg-gray-50 transition-colors" data-id="element-1562">
                  <td className="px-6 py-4 font-bold text-slate-900" data-id="element-1563">
                    {po.id}
                  </td>
                  <td className="px-6 py-4 font-medium text-gray-500" data-id="element-1564">
                    {po.date}
                  </td>
                  <td className="px-6 py-4 font-bold text-slate-900" data-id="element-1565">
                    ${po.amount.toLocaleString()}.00
                  </td>
                  <td className="px-6 py-4 text-gray-500 font-medium" data-id="element-1566">
                    {po.method}
                  </td>
                  <td className="px-6 py-4" data-id="element-1567">
                    <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${po.status === 'Settled' ? 'bg-emerald-50 text-emerald-600' : po.status === 'Processing' ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'}`} data-id="element-1568">
                      {po.status}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}